import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { CloudNavBar } from "../components/CloudNavBar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@ensafely/checker-ui";

interface EscalationStep {
  channel: string;
  delay_min: number;
}

interface EscalationPolicy {
  id?: number;
  name: string;
  steps: EscalationStep[];
  created_at?: string;
}

interface AlertChannel {
  name: string;
  type: string;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

interface PolicyFormProps {
  initial: EscalationPolicy | null;
  alertChannels: AlertChannel[];
  onSave: (policy: EscalationPolicy) => Promise<void>;
  onCancel: () => void;
}

function PolicyForm({ initial, alertChannels, onSave, onCancel }: PolicyFormProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [steps, setSteps] = useState<EscalationStep[]>(
    initial?.steps?.length ? initial.steps : [{ channel: "", delay_min: 0 }]
  );
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const addStep = () => setSteps((s) => [...s, { channel: "", delay_min: 0 }]);
  const removeStep = (i: number) => setSteps((s) => s.filter((_, idx) => idx !== i));
  const updateStep = (i: number, field: keyof EscalationStep, value: string | number) =>
    setSteps((s) => s.map((st, idx) => (idx === i ? { ...st, [field]: value } : st)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!name.trim()) { setError("Policy name is required."); return; }
    if (steps.length === 0) { setError("At least one step is required."); return; }
    for (let i = 0; i < steps.length; i++) {
      if (!steps[i].channel) { setError(`Step ${i + 1}: channel is required.`); return; }
      if (steps[i].delay_min < 0) { setError(`Step ${i + 1}: delay must be >= 0.`); return; }
    }
    setSaving(true);
    try {
      await onSave({ name: name.trim(), steps });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save policy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initial ? "Edit Escalation Policy" : "New Escalation Policy"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{error}</p>
          )}

          <label className="block">
            <span className="block text-sm font-medium mb-1">Policy Name *</span>
            <input
              type="text"
              className="w-full border rounded px-3 py-2 text-sm bg-background"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!initial}
              placeholder="e.g. Critical Alerts"
              required
            />
            {initial && (
              <p className="text-xs text-muted-foreground mt-1">Policy name cannot be changed after creation.</p>
            )}
          </label>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Escalation Steps</span>
              <button
                type="button"
                onClick={addStep}
                className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:opacity-90"
              >
                + Add Step
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Step 1 fires immediately on alert. Each subsequent step fires after the specified delay.
            </p>
            <div className="space-y-3">
              {steps.map((step, i) => (
                <div key={i} className="flex items-end gap-2 p-3 bg-muted/40 rounded border">
                  <div className="text-xs text-muted-foreground w-6 text-center pt-1 shrink-0">
                    {i + 1}
                  </div>
                  <label className="flex-1 block">
                    <span className="block text-xs text-muted-foreground mb-1">Alert Channel</span>
                    <select
                      className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                      value={step.channel}
                      onChange={(e) => updateStep(i, "channel", e.target.value)}
                      required
                    >
                      <option value="">Select channel...</option>
                      {alertChannels.map((ch) => (
                        <option key={ch.name} value={ch.name}>
                          {ch.name} ({ch.type})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="w-32 block">
                    <span className="block text-xs text-muted-foreground mb-1">
                      Delay (minutes)
                    </span>
                    <input
                      type="number"
                      min="0"
                      className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                      value={step.delay_min}
                      onChange={(e) => updateStep(i, "delay_min", Math.max(0, parseInt(e.target.value) || 0))}
                    />
                  </label>
                  {steps.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStep(i)}
                      className="text-destructive hover:opacity-70 text-sm pb-1.5"
                      title="Remove step"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-2 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm rounded border hover:bg-muted transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
              disabled={saving}
            >
              {saving ? "Saving..." : initial ? "Save Changes" : "Create Policy"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function EscalationPoliciesPage() {
  const [policies, setPolicies] = useState<EscalationPolicy[]>([]);
  const [alertChannels, setAlertChannels] = useState<AlertChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<EscalationPolicy | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [expandedPolicy, setExpandedPolicy] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [policiesData, channelsData] = await Promise.all([
        api.get<EscalationPolicy[]>("/escalation-policies"),
        api.get<AlertChannel[]>("/alert-channels").catch(() => [] as AlertChannel[]),
      ]);
      setPolicies(policiesData || []);
      setAlertChannels(channelsData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load escalation policies.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (policy: EscalationPolicy) => {
    await api.post("/escalation-policies", policy);
    setShowForm(false);
    setSuccess(`Policy "${policy.name}" created successfully.`);
    await loadData();
  };

  const handleEdit = async (policy: EscalationPolicy) => {
    await api.put(`/escalation-policies/${encodeURIComponent(editingPolicy!.name)}`, policy);
    setEditingPolicy(null);
    setSuccess(`Policy "${policy.name}" updated successfully.`);
    await loadData();
  };

  const handleDelete = async (name: string) => {
    try {
      await api.del(`/escalation-policies/${encodeURIComponent(name)}`);
      setConfirmDelete(null);
      setSuccess(`Policy "${name}" deleted.`);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete policy.");
      setConfirmDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <CloudNavBar />
      <div className="max-w-4xl mx-auto py-8 px-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Escalation Policies</h1>
          <button
            onClick={() => { setShowForm(true); setEditingPolicy(null); }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90"
          >
            New Policy
          </button>
        </div>

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <span className="flex-1">{error}</span>
            <button className="shrink-0 opacity-70 hover:opacity-100" onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {success && (
          <div className="mb-6 flex items-start gap-3 rounded-lg border border-green-400/40 bg-green-400/10 px-4 py-3 text-sm text-green-700 dark:text-green-400">
            <span className="flex-1">{success}</span>
            <button className="shrink-0 opacity-70 hover:opacity-100" onClick={() => setSuccess(null)}>&times;</button>
          </div>
        )}

        {loading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : policies.length === 0 ? (
          <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
            <p className="text-lg font-medium mb-2">No escalation policies yet</p>
            <p className="text-sm mb-4">
              Create your first escalation policy to automatically notify multiple channels in sequence when a check fails.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-medium hover:opacity-90"
            >
              Create First Policy
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {policies.map((policy) => (
              <div key={policy.name} className="bg-card rounded-lg shadow">
                {/* Policy header */}
                <div className="px-6 py-4 flex items-center gap-4">
                  <button
                    className="flex-1 flex items-center gap-3 text-left"
                    onClick={() =>
                      setExpandedPolicy(expandedPolicy === policy.name ? null : policy.name)
                    }
                  >
                    <span className="font-medium">{policy.name}</span>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {policy.steps?.length ?? 0} step{(policy.steps?.length ?? 0) !== 1 ? "s" : ""}
                    </span>
                    {policy.created_at && (
                      <span className="text-xs text-muted-foreground ml-auto">
                        Created {formatDate(policy.created_at)}
                      </span>
                    )}
                    <span className="text-muted-foreground text-xs ml-2">
                      {expandedPolicy === policy.name ? "▲" : "▼"}
                    </span>
                  </button>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => { setEditingPolicy(policy); setShowForm(false); }}
                      className="text-xs px-3 py-1.5 border rounded hover:bg-muted transition-colors"
                    >
                      Edit
                    </button>
                    {confirmDelete === policy.name ? (
                      <div className="flex gap-1 items-center">
                        <button
                          onClick={() => handleDelete(policy.name)}
                          className="text-xs px-2 py-1.5 bg-destructive text-destructive-foreground rounded hover:opacity-90"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDelete(null)}
                          className="text-xs px-2 py-1.5 border rounded hover:bg-muted"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDelete(policy.name)}
                        className="text-xs px-3 py-1.5 border border-destructive/30 text-destructive rounded hover:bg-destructive/10 transition-colors"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>

                {/* Policy steps (expanded) */}
                {expandedPolicy === policy.name && (
                  <div className="border-t px-6 py-4">
                    {(!policy.steps || policy.steps.length === 0) ? (
                      <p className="text-sm text-muted-foreground">No steps defined.</p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-3">
                          Escalation Steps
                        </p>
                        {policy.steps.map((step, i) => (
                          <div key={i} className="flex items-center gap-3 text-sm">
                            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium shrink-0">
                              {i + 1}
                            </span>
                            <span className="font-medium">{step.channel}</span>
                            <span className="text-muted-foreground">
                              {i === 0
                                ? "fires immediately"
                                : `after ${step.delay_min} minute${step.delay_min !== 1 ? "s" : ""}`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Create form */}
        {showForm && (
          <PolicyForm
            initial={null}
            alertChannels={alertChannels}
            onSave={handleCreate}
            onCancel={() => setShowForm(false)}
          />
        )}

        {/* Edit form */}
        {editingPolicy && (
          <PolicyForm
            initial={editingPolicy}
            alertChannels={alertChannels}
            onSave={handleEdit}
            onCancel={() => setEditingPolicy(null)}
          />
        )}
      </div>
    </div>
  );
}
